import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import styles from './HeroSection.module.scss';

const HeroSection: React.FC = () => {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section className={`${styles.hero} dark-section`} ref={ref} id="hero">
      {/* Decorative background elements */}
      <div className="dot-grid" />
      <div className="concentric-circles" />
      
      {/* Abstract @ Symbol */}
      <div className={styles.abstractSymbol}>
        <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M50 85C30.67 85 15 69.33 15 50C15 30.67 30.67 15 50 15C69.33 15 85 30.67 85 50C85 59.665 81.0833 66.5 75 66.5C68.9167 66.5 65 59.665 65 50C65 41.7157 58.2843 35 50 35C41.7157 35 35 41.7157 35 50C35 58.2843 41.7157 65 50 65C58.2843 65 65 62 65 50" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </div>

      <motion.div className={`container ${styles.content}`} style={{ y, opacity }}>
        <motion.div
          className={styles.eyebrow}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          TECHNOLOGY • DESIGN • DIGITAL SOLUTIONS
        </motion.div>

        <h1 className={styles.headline}>
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}>BUILDING</motion.div>
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}>TECHNOLOGY</motion.div>
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}>THAT MOVES</motion.div>
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25, ease: [0.22, 1, 0.36, 1] }} className={styles.gradientText}>BUSINESSES</motion.div>
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}>FORWARD.</motion.div>
        </h1>

        <motion.p
          className={styles.subtitle}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          From websites and mobile applications to AI, design, marketing, SEO, hosting, and brand identity — Arkatechs helps businesses turn ideas into digital experiences.
        </motion.p>

        <motion.div
          className={styles.actions}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <Link to="/services" className={`${styles.btn} ${styles.btnPrimary}`}>
            EXPLORE SERVICES <ArrowRight size={16} />
          </Link>
          <Link to="/work" className={`${styles.btn} ${styles.btnSecondary}`}>
            VIEW OUR WORK <ArrowRight size={16} />
          </Link>
        </motion.div>
      </motion.div>
      
      {/* Torn Edge Transition to next section */}
      <div className="torn-edge-bottom" />
    </section>
  );
};

export default HeroSection;
